import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useFocusTrap } from "../../../../hook/useFocusTrap";
import { REGISTRATION_KEYBOARD_SCOPE_ATTR } from "../../../RegistrationLayout/registrationFocusStyles";
import {
  RELATIONSHIP_OPTIONS,
  MANUAL_RELATIONSHIP_OPTIONS,
} from "../insuranceProviders";
import {
  ModalOverlay,
  ModalPanel,
  ModalHeader,
  ModalHeaderText,
  ModalTitle,
  ModalCloseBtn,
  ModalBody,
  GotItBtn,
  ConfirmScrollArea,
  ConfirmIntro,
  DetailList,
  DetailRow,
  DetailLabel,
  DetailValue,
  ConfirmActions,
  CancelBtn,
} from "./InsuranceDetailsConfirmModal.style";

const LABEL_FALLBACKS = {
  NUTRITION_INS_CONFIRM_LBL_TITLE: "Confirm insurance details",
  NUTRITION_INS_CONFIRM_DESC:
    "Please review the insurance information below before continuing.",
  NUTRITION_INS_CONFIRM_BTN_CONFIRM: "Confirm & continue",
  NUTRITION_INS_CONFIRM_BTN_EDIT: "Edit details",
  NUTRITION_COMMON_LBL_INSURANCE_PROVIDER: "Insurance provider",
  NUTRITION_INSDISC_LBL_FIRST_NAME: "First name",
  NUTRITION_INSDISC_LBL_LAST_NAME: "Last name",
  DATE_OF_BIRTH: "Date of birth",
  MEMBER_ID: "Member ID",
  NUTRITION_COMMON_LBL_GROUP_NUMBER: "Group number",
  NUTRITION_INSDISC_LBL_RELATIONSHIP: "Relationship",
  NUTRITION_INSMANUAL_LBL_HOLDER_FIRST_NAME: "Policy holder first name",
  NUTRITION_INSMANUAL_LBL_HOLDER_LAST_NAME: "Policy holder last name",
  NUTRITION_INSMANUAL_LBL_HOLDER_DOB: "Policy holder date of birth",
  SELF: "Self",
  DEPENDENT: "Dependent",
  NUTRITION_INSMANUAL_LBL_RELATIONSHIP_SPOUSE_PARTNER: "Spouse / Partner",
};

const translateWithFallback = (t, key) => {
  const translated = t(key);
  if (translated !== key) {
    return translated;
  }
  return LABEL_FALLBACKS[key] || translated;
};

const getRelationshipLabel = (relationship, t) => {
  const value = String(relationship ?? "").trim().toLowerCase();
  const option =
    RELATIONSHIP_OPTIONS.find((item) => item.value === value) ||
    MANUAL_RELATIONSHIP_OPTIONS.find((item) => item.value === value);
  if (!option?.labelKey) {
    return relationship || "—";
  }
  return translateWithFallback(t, option.labelKey);
};

export const buildInsuranceConfirmRows = (details = {}, t) => {
  const safeDetails = details && typeof details === "object" ? details : {};
  const rows = [
    {
      key: "provider",
      label: translateWithFallback(t, "NUTRITION_COMMON_LBL_INSURANCE_PROVIDER"),
      value: safeDetails.providerName || safeDetails.provider || "—",
    },
    {
      key: "firstName",
      label: translateWithFallback(t, "NUTRITION_INSDISC_LBL_FIRST_NAME"),
      value: safeDetails.firstName || "—",
    },
    {
      key: "lastName",
      label: translateWithFallback(t, "NUTRITION_INSDISC_LBL_LAST_NAME"),
      value: safeDetails.lastName || "—",
    },
    {
      key: "dob",
      label: translateWithFallback(t, "DATE_OF_BIRTH"),
      value: safeDetails.dob || "—",
    },
    {
      key: "memberId",
      label: translateWithFallback(t, "MEMBER_ID"),
      value: safeDetails.memberId || "—",
    },
  ];

  if (safeDetails.groupNumber) {
    rows.push({
      key: "groupNumber",
      label: translateWithFallback(t, "NUTRITION_COMMON_LBL_GROUP_NUMBER"),
      value: safeDetails.groupNumber,
    });
  }

  rows.push({
    key: "relationship",
    label: translateWithFallback(t, "NUTRITION_INSDISC_LBL_RELATIONSHIP"),
    value: getRelationshipLabel(safeDetails.relationship, t),
  });

  const relationship = String(safeDetails.relationship ?? "")
    .trim()
    .toLowerCase();
  const showHolder =
    relationship === "spouse" || relationship === "dependent";

  if (showHolder) {
    rows.push(
      {
        key: "holderFirstName",
        label: translateWithFallback(
          t,
          "NUTRITION_INSMANUAL_LBL_HOLDER_FIRST_NAME"
        ),
        value:
          safeDetails.policyHolderFirstName ||
          safeDetails.holderFirstName ||
          "—",
      },
      {
        key: "holderLastName",
        label: translateWithFallback(
          t,
          "NUTRITION_INSMANUAL_LBL_HOLDER_LAST_NAME"
        ),
        value:
          safeDetails.policyHolderLastName ||
          safeDetails.holderLastName ||
          "—",
      },
      {
        key: "holderDob",
        label: translateWithFallback(t, "NUTRITION_INSMANUAL_LBL_HOLDER_DOB"),
        value: safeDetails.policyHolderDob || safeDetails.holderDob || "—",
      }
    );
  }

  return rows;
};

const InsuranceDetailsConfirmModal = ({
  isOpen,
  details,
  confirmLabel,
  onConfirm,
  onClose,
  isConfirming = false,
}) => {
  const { t } = useTranslation();
  const confirmButtonRef = useRef(null);
  const panelRef = useRef(null);

  useFocusTrap({
    isOpen: Boolean(isOpen && details),
    containerRef: panelRef,
    initialFocusRef: confirmButtonRef,
    onEscape: isConfirming ? undefined : onClose,
  });

  useEffect(() => {
    if (!isOpen || !details) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen, details]);

  if (!isOpen || !details || typeof document === "undefined") {
    return null;
  }

  const rows = buildInsuranceConfirmRows(details, t);
  const title = translateWithFallback(t, "NUTRITION_INS_CONFIRM_LBL_TITLE");
  const confirmText =
    confirmLabel ||
    translateWithFallback(t, "NUTRITION_INS_CONFIRM_BTN_CONFIRM");

  return createPortal(
    <ModalOverlay
      {...REGISTRATION_KEYBOARD_SCOPE_ATTR}
      role="presentation"
      onClick={(event) => {
        if (event.target === event.currentTarget && !isConfirming) {
          onClose?.();
        }
      }}
    >
      <ModalPanel
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="insurance-details-confirm-title"
      >
        <ModalHeader>
          <ModalHeaderText>
            <ModalTitle id="insurance-details-confirm-title">{title}</ModalTitle>
            <ConfirmIntro>
              {translateWithFallback(t, "NUTRITION_INS_CONFIRM_DESC")}
            </ConfirmIntro>
          </ModalHeaderText>
          <ModalCloseBtn
            type="button"
            aria-label={t("NUTRITION_COMMON_ARIA_CLOSE", {
              defaultValue: "Close",
            })}
            onClick={onClose}
            disabled={isConfirming}
          >
            ×
          </ModalCloseBtn>
        </ModalHeader>
        <ModalBody>
          <ConfirmScrollArea>
            <DetailList>
              {rows.map((row) => (
                <DetailRow key={row.key}>
                  <DetailLabel>{row.label}</DetailLabel>
                  <DetailValue>{row.value}</DetailValue>
                </DetailRow>
              ))}
            </DetailList>
          </ConfirmScrollArea>
          <ConfirmActions>
            <CancelBtn
              type="button"
              onClick={onClose}
              disabled={isConfirming}
            >
              {translateWithFallback(t, "NUTRITION_INS_CONFIRM_BTN_EDIT")}
            </CancelBtn>
            <GotItBtn
              ref={confirmButtonRef}
              type="button"
              onClick={onConfirm}
              disabled={isConfirming}
              aria-busy={isConfirming}
            >
              {confirmText}
            </GotItBtn>
          </ConfirmActions>
        </ModalBody>
      </ModalPanel>
    </ModalOverlay>,
    document.body
  );
};

export default InsuranceDetailsConfirmModal;
